'use client'

import { useEffect, useState, useRef, Suspense } from 'react'
import { useSearchParams } from 'next/navigation'
import { SubscriptionModal } from './subscription-modal'
import { getSubscription } from '@/lib/api-client'
import { useAuth } from '@/lib/auth'

interface SubscriptionGuardProps {
  children: React.ReactNode
}

function SubscriptionGuardInner({ children }: SubscriptionGuardProps) {
  const { user, isInitializing } = useAuth()
  const [isChecking, setIsChecking] = useState(true)
  const [hasSubscription, setHasSubscription] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [isPolling, setIsPolling] = useState(false)
  const searchParams = useSearchParams()
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    // Wait for AuthProvider to resolve before making authenticated API calls
    if (isInitializing || !user) return

    checkSubscription()

    // Check for Stripe callback (for immediate recheck)
    const subscriptionComplete = searchParams.get('subscription_complete')
    const subscriptionCanceled = searchParams.get('subscription_canceled')

    if (subscriptionComplete === 'true' || subscriptionCanceled === 'true') {
      // Immediately check subscription status
      checkSubscription()
      // Clear URL params
      window.history.replaceState({}, '', '/dashboard')
    }

    // Cleanup interval on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [searchParams, isInitializing, user])

  const checkSubscription = async () => {
    try {
      const subscription = await getSubscription()

      // Check for active subscription (paid via Stripe OR free plan)
      // Free subscriptions have empty stripe_subscription_id but status='active'
      const hasActiveSubscription = subscription?.status === 'active'

      setHasSubscription(hasActiveSubscription)

      if (!hasActiveSubscription) {
        setShowModal(true)
        // Start polling for subscription creation
        if (!pollingIntervalRef.current) {
          startPolling()
        }
      } else {
        setShowModal(false)
        // Stop polling if subscription found
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
      }
    } catch (error) {
      console.error('Subscription check failed:', error)
      setShowModal(true) // Show modal on error
    } finally {
      setIsChecking(false)
    }
  }

  const startPolling = () => {
    setIsPolling(true)

    // Poll every 3 seconds to check if subscription was created
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const subscription = await getSubscription()

        // Check for active subscription (paid via Stripe OR free plan)
        const hasActiveSubscription = subscription?.status === 'active'

        if (hasActiveSubscription) {
          // Subscription found - stop polling and close modal
          setHasSubscription(true)
          setShowModal(false)
          setIsPolling(false)
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
        }
      } catch (error) {
        console.error('Polling subscription check failed:', error)
      }
    }, 3000) // Check every 3 seconds
  }

  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Checking subscription...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      {children}
      {showModal && !hasSubscription && (
        <SubscriptionModal
          onComplete={() => {
            setShowModal(false)
            checkSubscription()
          }}
          onClose={() => {}} // Cannot close - no-op
          isPolling={isPolling}
        />
      )}
    </>
  )
}

export function SubscriptionGuard({ children }: SubscriptionGuardProps) {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    }>
      <SubscriptionGuardInner>{children}</SubscriptionGuardInner>
    </Suspense>
  )
}
